import React from 'react';
import {
  GROPER_CARD,
  GROPER_IMAGE_960,
  GROPER_IMAGE_1600,
  GROPER_IMAGE_WIDTH,
  GROPER_IMAGE_HEIGHT,
} from '../content/groperStory';

interface GroperPinnedCardProps {
  onReadMore: () => void;
}

/**
 * Static CLUB HISTORY pinned card for "Tumbulgum's Groper". Takes the pinned
 * spotlight slot at the top of the landing page's announcements section.
 *
 * Layout: stacked below lg (image on top); side by side from lg (1024px) up,
 * image in the left 46%, text on the right. The text is sized so its column is
 * no taller than the image, so the card height is the image height and there
 * is no letterboxing. Below lg the text column is too narrow for that.
 *
 * The image is the whole clubhouse sign, so it is never cropped: block w-full
 * h-auto, no object-fit and no fixed height. It is served from resized copies
 * (960w / 1600w) rather than the 1.4 MB master.
 */
const GroperPinnedCard: React.FC<GroperPinnedCardProps> = ({ onReadMore }) => (
  <article
    aria-labelledby="groper-card-heading"
    className="bg-white rounded-2xl shadow-lg overflow-hidden lg:flex lg:items-center"
  >
    {/* Framed-print treatment: inset from the card edge, dark frame, soft
        shadow. The frame wraps the whole image, so nothing is cropped. */}
    <div className="w-full p-3 pb-0 lg:p-3 lg:pr-0 lg:w-[48%] lg:flex-none">
      <img
        src={GROPER_IMAGE_960}
        srcSet={`${GROPER_IMAGE_960} 960w, ${GROPER_IMAGE_1600} 1600w`}
        sizes="(min-width: 1024px) 540px, 100vw"
        width={GROPER_IMAGE_WIDTH}
        height={GROPER_IMAGE_HEIGHT}
        alt={GROPER_CARD.imageAlt}
        loading="lazy"
        decoding="async"
        className="block w-full h-auto rounded-md border-4 border-[#1a1a1a] shadow-[0_4px_14px_rgba(0,0,0,0.25)]"
      />
    </div>
    <div className="p-4 md:p-5 lg:flex-1 flex flex-col justify-center">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="bg-[#159A63] text-white text-xs font-extrabold tracking-wider uppercase px-3.5 py-1.5 rounded-full">
          {GROPER_CARD.pill}
        </span>
        <span className="bg-gray-100 text-gray-600 text-xs font-bold tracking-wider uppercase px-3 py-1.5 rounded-full">
          {GROPER_CARD.tag}
        </span>
      </div>
      <h3
        id="groper-card-heading"
        className="text-2xl xl:text-[1.7rem] font-extrabold text-[#1d2e61] leading-tight text-balance mb-2"
      >
        {GROPER_CARD.headline}
      </h3>
      <p className="text-gray-700 leading-relaxed mb-4">{GROPER_CARD.teaser}</p>
      <div className="flex items-center justify-between gap-4 text-sm">
        <span className="text-gray-500">{GROPER_CARD.dateLabel}</span>
        <button
          type="button"
          onClick={onReadMore}
          aria-label="Read more: Tumbulgum's Groper"
          className="inline-flex min-h-[44px] items-center text-[#1d2e61] font-bold hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1d2e61] rounded"
        >
          Read more →
        </button>
      </div>
    </div>
  </article>
);

export default GroperPinnedCard;
